import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import moment from "moment";
import { fetchMyHomeworks } from "./redux/myHomeworksThunk";
import { fetchTeacherTimetable } from "../../TimeTable/redux/myTimetableSlice";
import { fetchSessionsThunk } from "../../../AcademicCalendar/redux/academicCalendarSlice";
import { getSyllabusThunk } from "../../redux/syllabusthunk";
import { generateLessonPlan } from "./generateLessonPlan";
import { getMasterDataByUserId } from "../../../../pages/masterData/redux/masterDataThunk";
import userStore from "../../../../zustand/userStore";
import { fetchTeachers } from "../../../SchoolModule/teachers/redux/teacherSlice";

const AllHomeworks = ({ teacherId }) => {
  const dispatch = useDispatch();
  const {
    data: homeworks,
    loading,
    error,
  } = useSelector((state) => state.homeworks);
  const { syllabusData } = useSelector((state) => state.syllabus);
  const { teacherPeriods } = useSelector((state) => state.myTimetable);
  const { sessions } = useSelector((state) => state.academicCalendar);
  const { teachers } = useSelector((state) => state.teachers);

  const [weeklyPlan, setWeeklyPlan] = useState([]);
  const [formData, setFormData] = useState({
    selectedClass: "",
    selectedSection: "",
  });
  const [expanded, setExpanded] = useState(false);

  const formatText = (text) => {
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const masterData = useSelector((state) => state.masterData);
  const schoolId = masterData?.school_id;
  const userData = userStore((state) => state.user);

  const [selectedTeacherId, setSelectedTeacherId] = useState("");

  useEffect(() => {
    dispatch(getMasterDataByUserId(userData?.id));
  }, [dispatch]);

  useEffect(() => {
    if (schoolId) {
      dispatch(fetchTeachers());
    }
  }, [dispatch, schoolId]);

  useEffect(() => {
    if (selectedTeacherId) {
      dispatch(fetchMyHomeworks(selectedTeacherId));
      dispatch(fetchTeacherTimetable(selectedTeacherId));
      dispatch(fetchSessionsThunk());
      dispatch(getSyllabusThunk(selectedTeacherId));
    }
  }, [dispatch, selectedTeacherId]);

  useEffect(() => {
    if (
      !syllabusData.length ||
      !teacherPeriods.classes?.length ||
      !sessions.length
    )
      return;

    const { weeks } = generateLessonPlan({
      syllabusData,
      sessions,
      teacherPeriods,
      selectedClass: "",
      selectedSubject: "",
      selectedSection: "",
    });

    setWeeklyPlan(weeks);
  }, [syllabusData, teacherPeriods, sessions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const findHomework = (row) => {
    const chapterRows = weeklyPlan
      .flat()
      .filter(
        (r) =>
          String(r.class_id) === String(row.class_id) &&
          String(r.period?.section_id) === String(row.period?.section_id) &&
          String(r.period?.subject_id) === String(row.period?.subject_id) &&
          r.chapter.trim().toLowerCase() === row.chapter.trim().toLowerCase()
      )
      .sort((a, b) => new Date(a.period.date) - new Date(b.period.date));

    const hw = homeworks?.find(
      (hwItem) =>
        String(hwItem.class_id) === String(row.class_id) &&
        String(hwItem.section_id) === String(row.period?.section_id) &&
        String(hwItem.subject_id) === String(row.period?.subject_id) &&
        hwItem.chapter_name.trim().toLowerCase() ===
          row.chapter.trim().toLowerCase() &&
        String(hwItem.period_number) === String(row.sno)
    );

    if (!hw) return null;

    const matchedRow = chapterRows.find(
      (r) => String(r.sno) === String(hw.period_number)
    );

    return matchedRow ? { ...hw, date: matchedRow.period.date } : null;
  };

  const allRows = weeklyPlan.flat();

  // ✅ Apply class & section filter
  const filteredRows = allRows
    .map((row) => {
      const hw = findHomework(row);
      return hw ? { row, hw } : null;
    })
    .filter(Boolean)
    .filter(({ hw }) => {
      if (formData.selectedClass && hw.class_id !== formData.selectedClass)
        return false;
      if (
        formData.selectedSection &&
        hw.section_id !== formData.selectedSection
      )
        return false;
      return true;
    });

  return (
    <div style={{ maxWidth: 1300, margin: "auto" }}>
      <div className="filter-container mb-3">
        <label>
          <select
            className="form-select"
            value={selectedTeacherId}
            onChange={(e) => {
              setSelectedTeacherId(e.target.value);
            }}
          >
            <option value="">Select a teacher</option>
            {Array.isArray(teachers) && teachers.length > 0 ? (
              teachers.map((teacher) => (
                <option key={teacher.teacher_id} value={teacher.teacher_id}>
                  {teacher.name}
                </option>
              ))
            ) : (
              <option disabled>No teachers found</option>
            )}
          </select>
        </label>
      </div>
      <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
        {selectedTeacherId && (
          <>
            <div>
              <label>Select Class</label>
              <select
                name="selectedClass"
                value={formData.selectedClass}
                onChange={handleChange}
                style={{ padding: "5px", marginLeft: "10px" }}
              >
                <option value="">-- Select Class --</option>
                {teacherPeriods?.classes?.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Select Section</label>
              <select
                name="selectedSection"
                value={formData.selectedSection}
                onChange={handleChange}
                style={{ padding: "5px", marginLeft: "10px" }}
              >
                <option value="">-- Select Section --</option>
                {teacherPeriods?.classes
                  ?.find((cls) => cls.class_id == formData.selectedClass)
                  ?.sections?.map((sec) => (
                    <option key={sec.section_id} value={sec.section_id}>
                      {sec.section_name}
                    </option>
                  ))}
              </select>
            </div>
          </>
        )}
      </div>

      <TableContainer component={Paper} sx={{ width: "100%", boxShadow: 3 }}>
        {loading && <CircularProgress sx={{ m: 2 }} />}
        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                <TableCell>Date</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Chapter</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Homework Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map(({ row, hw }, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {moment(hw.date).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell>{moment(hw.date).format("dddd")}</TableCell>
                    <TableCell>{hw.class_name}</TableCell>
                    <TableCell>{hw.section_name}</TableCell>
                    <TableCell>{hw.subject_name}</TableCell>
                    <TableCell>{hw.chapter_name}</TableCell>
                    <TableCell>{hw.period_number}</TableCell>
                    {/* <TableCell>{hw.homework_details}</TableCell> */}
                    <TableCell>
                      {expanded
                        ? formatText(hw.homework_details)
                        : formatText(hw.homework_details.slice(0, 30))}

                      {hw.homework_details.length > 30 && (
                        <span
                          style={{
                            color: "blue",
                            cursor: "pointer",
                            marginLeft: "5px",
                          }}
                          onClick={() => setExpanded(!expanded)}
                        >
                          {expanded ? "View Less" : "View More"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  );
};

export default AllHomeworks;
